/* ==========================================================================
   LIVVY — SHARED SECTION BACKDROP
   --------------------------------------------------------------------------
   Paints ONE background image behind a run of consecutive sections.

   Why this is not pure CSS: to slice a single tall image across N sections you
   need each section's rendered height, which Liquid cannot know. Cutting the
   image into N files drifts the moment any section's content reflows. So the
   image is painted once, into a layer that spans the whole group, and the
   group is re-measured whenever anything resizes.

   Usage: give each section in the run the same "Shared background group" name.
   The first section in the run supplies the image.
   ========================================================================== */

(() => {
  const GROUP_ATTR = 'data-lv-bg-group';

  class LvBackdrop {
    constructor(name, sections) {
      this.name = name;
      this.sections = sections;
      this.leader = sections[0];

      this.layer = document.createElement('div');
      this.layer.className = 'lv-backdrop';
      this.layer.setAttribute('aria-hidden', 'true');

      const desktop = this.leader.dataset.lvBgImage;
      const mobile = this.leader.dataset.lvBgImageMobile || desktop;
      this.desktop = desktop;
      this.mobile = mobile;

      // "blooms" mode paints soft radial washes from CSS instead of a file, so
      // the layer still has to be built and measured even with no image set.
      this.blooms = this.leader.dataset.lvBgMode === 'blooms';
      if (!desktop && !this.blooms) return;

      // The layer lives inside the first section and overflows downward across
      // its siblings. Those siblings paint later, so their (transparent)
      // backgrounds let it show through while their content stays on top.
      this.leader.style.position = 'relative';
      this.leader.prepend(this.layer);

      this.measure = this.measure.bind(this);

      this.observer = new ResizeObserver(this.measure);
      this.sections.forEach((section) => this.observer.observe(section));

      window.addEventListener('resize', this.measure, { passive: true });
      window.addEventListener('load', this.measure);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.measure);

      this.measure();
    }

    measure() {
      const last = this.sections[this.sections.length - 1];
      if (!this.leader.isConnected || !last.isConnected) return;

      const top = this.leader.getBoundingClientRect().top + window.scrollY;
      const bottom = last.getBoundingClientRect().bottom + window.scrollY;
      const height = Math.max(0, Math.round(bottom - top));

      this.layer.style.height = `${height}px`;

      if (this.blooms) return; // the gradients come from the section's own CSS

      const useMobile = window.matchMedia('(max-width: 999px)').matches;
      const image = useMobile ? this.mobile : this.desktop;
      this.layer.style.backgroundImage = `url(${image})`;
    }
  }

  const build = () => {
    const groups = new Map();

    document.querySelectorAll(`[${GROUP_ATTR}]`).forEach((el) => {
      const name = (el.getAttribute(GROUP_ATTR) || '').trim();
      if (!name) return;
      if (!groups.has(name)) groups.set(name, []);
      groups.get(name).push(el);
    });

    groups.forEach((sections, name) => {
      // Keep them in document order so "first" and "last" are meaningful
      sections.sort((a, b) =>
        a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
      );
      new LvBackdrop(name, sections);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', build);
  } else {
    build();
  }

  // The theme editor re-renders sections one at a time; rebuild from scratch
  // so the group boundaries stay correct.
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('.lv-backdrop').forEach((el) => el.remove());
    build();
  });
})();
